import { z } from 'zod';
import { UserType } from '../user-types';
import { DateOrIsoStringSchema } from './date-or-iso';

const AdminRoleOptionSchema = z.object({
  id: z.number(),
  slug: z.string(),
  name: z.string(),
  description: z.string().nullable(),
});

export const OwnerOptionSchema = z.object({
  ownerEmail: z.string(),
  slackUserId: z.string(),
  userType: z.enum(UserType).nullable(),
  /** First names of the managers above them in Salesforce, top down; absent without a Salesforce user. */
  reportingLine: z.array(z.string()).optional(),
});
export type OwnerOption = z.infer<typeof OwnerOptionSchema>;

export const DemoOptionSchema = z.object({
  slackUserId: z.string(),
  label: z.string(),
  manifestKey: z.string(),
});
export type DemoOption = z.infer<typeof DemoOptionSchema>;

const ImpersonationPreviewMeetingSchema = z.object({
  startsAt: DateOrIsoStringSchema,
  title: z.string(),
  accountId: z.string().nullable(),
  accountName: z.string().nullable(),
});

/** What the ⌘I palette shows about the highlighted person. Each fact is null when Terra has nothing to read for it. */
export const ImpersonationPreviewResponseSchema = z.object({
  slackUserId: z.string(),
  title: z.string().nullable(),
  team: z.string().nullable(),
  accounts: z.number().nullable(),
  signalsToDo: z.number().nullable(),
  lastActiveAt: DateOrIsoStringSchema.nullable(),
  /** The next three, soonest first. */
  meetings: z.array(ImpersonationPreviewMeetingSchema),
  /** The last three before now, latest first. */
  pastMeetings: z.array(ImpersonationPreviewMeetingSchema),
});
export type ImpersonationPreviewResponse = z.infer<typeof ImpersonationPreviewResponseSchema>;

/** Everyone's sort values for the ⌘I list, one read for the whole roster. */
export const ImpersonationSortFactsResponseSchema = z.object({
  people: z.array(z.object({
    slackUserId: z.string(),
    nextMeetings: z.number(),
    lastMeetingAt: DateOrIsoStringSchema.nullable(),
    lastActiveAt: DateOrIsoStringSchema.nullable(),
  })),
});
export type ImpersonationSortFactsResponse = z.infer<typeof ImpersonationSortFactsResponseSchema>;

const AdminDelegationSchema = z.object({
  id: z.number(),
  role: z.object({ id: z.number(), slug: z.string(), name: z.string(), description: z.string().nullable() }),
  expiresAt: DateOrIsoStringSchema.nullable(),
});

const RoleGrantSummarySchema = z.object({
  id: z.number(),
  slug: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  grantedAt: DateOrIsoStringSchema,
  grantedById: z.number().nullable(),
});

const UserContextSummarySchema = z.object({
  id: z.number(),
  slackUserId: z.string(),
  ownerEmail: z.string().nullable(),
  roles: z.array(RoleGrantSummarySchema),
});

const DelegationSummarySchema = AdminDelegationSchema.extend({
  delegatorId: z.number(),
  delegateId: z.number(),
  createdAt: DateOrIsoStringSchema,
  createdById: z.number().nullable(),
  notes: z.string().nullable(),
});

export const AdminMappingResponseSchema = z.object({
  ok: z.literal(true),
  user: z.object({ id: z.number(), slackUserId: z.string(), ownerEmail: z.string().nullable() }),
  delegation: DelegationSummarySchema.nullable(),
  roles: z.array(RoleGrantSummarySchema),
  actingUser: UserContextSummarySchema.extend({
    userType: z.enum(UserType),
    salesforceConnectedAt: DateOrIsoStringSchema.nullable(),
  }),
});

export const AdminMeResponseSchema = z.object({
  id: z.number(),
  slackUserId: z.string(),
  actingSlackUserId: z.string().optional(),
  ownerEmail: z.string().nullable().optional(),
  prefDaysToShow: z.number().optional(),
  roles: z.array(RoleGrantSummarySchema),
  actingUser: UserContextSummarySchema.nullable().optional(),
  delegation: AdminDelegationSchema.nullable().optional(),
  ownerOptions: z.array(OwnerOptionSchema),
  roleOptions: z.array(AdminRoleOptionSchema),
  // Demo identities, kill-switch ON only. Always present: [] when none qualify or the
  // flag is OFF for all of them. Never part of `ownerOptions`.
  demoOptions: z.array(DemoOptionSchema),
});
export type AdminMeResponse = z.infer<typeof AdminMeResponseSchema>;

const RoleMemberSchema = z.object({
  id: z.number(),
  slackUserId: z.string(),
  ownerEmail: z.string().nullable(),
  userType: z.enum(UserType),
  grantedAt: DateOrIsoStringSchema,
  grantedBy: z
    .object({ id: z.number(), slackUserId: z.string(), ownerEmail: z.string().nullable() })
    .nullable(),
});
export type RoleMember = z.infer<typeof RoleMemberSchema>;

export const AdminRoleRosterResponseSchema = z.object({
  roles: z.array(
    z.object({
      id: z.number(),
      slug: z.string(),
      name: z.string(),
      description: z.string().nullable(),
      memberCount: z.number(),
      members: z.array(RoleMemberSchema),
    }),
  ),
});
export type AdminRoleRosterResponse = z.infer<typeof AdminRoleRosterResponseSchema>;
export type RoleRosterEntry = AdminRoleRosterResponse['roles'][number];

export const AdminRoleUserResponseSchema = z.object({
  id: z.number(),
  slackUserId: z.string(),
  ownerEmail: z.string().nullable(),
  roles: z.array(RoleGrantSummarySchema),
});
export type AdminRoleUserResponse = z.infer<typeof AdminRoleUserResponseSchema>;

export const BulkUserTypeUpdateResponseSchema = z.object({ updated: z.number() });

export const AdminRoleMutationResponseSchema = z.object({
  user: AdminRoleUserResponseSchema,
  context: z
    .object({
      authUser: UserContextSummarySchema,
      actingUser: UserContextSummarySchema,
      delegation: AdminDelegationSchema.nullable(),
    })
    .nullable(),
});
export type AdminRoleMutationResponse = z.infer<typeof AdminRoleMutationResponseSchema>;
